import { API, postJSON } from "../../api.js";
import { wireCommandButton } from "../../app/commands.js";
import { $, esc, fmtMoney, fmtNum } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { openAmountModal, sliderField, toast } from "../../ui/modals.js";
import { openExpandMarketModal } from "./government.js";

// ---------------------------------------------------------------- Operations (Products)

// ---------------------------------------------------------------- Operations (Products) — deep rebuild

const HEALTH_COLOR = (score) => score >= 70 ? "pos" : score >= 40 ? "" : "neg";

async function renderOperationsBriefing() {
  const el = $("operations-briefing");
  if (!el) return;
  el.innerHTML = `<p style="color:var(--text-mid); font-size:12.5px;">Preparing operations briefing…</p>`;
  let data;
  try {
    data = await fetch(`${API}/operations-report`).then((x) => x.json());
  } catch (e) {
    el.innerHTML = "";
    return;
  }
  const s = data.report.summary;
  const stat = (label, val) => val === null || val === undefined ? "" : `
    <div class="risk-stat-box" style="min-width:120px;">
      <div class="risk-stat-label">${esc(label)}</div>
      <div class="risk-stat-value" style="font-size:17px;">${val}</div>
    </div>`;
  el.innerHTML = `
    <h3 style="font-family:var(--font-display); font-size:16px; margin-bottom:12px;">Operations Briefing — Month ${data.report.month}</h3>
    <div class="risk-stat-row" style="margin-bottom:16px;">
      ${stat("Active Products", s.active_products)}
      ${stat("Combined Revenue", fmtMoney(s.monthly_revenue) + "/mo")}
      ${stat("Combined Profit", fmtMoney(s.monthly_profit) + "/mo")}
      ${stat("Total Customers", s.total_customers.toLocaleString("en-US"))}
      ${s.avg_health_score !== null ? stat("Avg Health", s.avg_health_score + "/100") : ""}
      ${stat("Factories", s.factory_count)}
    </div>
    ${data.report.items.map((item) => `
      <div class="alert-card ${item.priority === "High" ? "critical" : item.priority === "Medium" ? "warning" : "info"}" style="align-items:flex-start;">
        <div>
          <div class="alert-category">${esc(item.priority)} Priority</div>
          <div class="alert-message">${esc(item.observation)}</div>
          <div style="color:var(--brass); font-size:12px; margin-top:5px;">→ ${esc(item.recommendation)}</div>
        </div>
      </div>
    `).join("")}
    <button id="ops-briefing-toggle" style="margin-top:10px;">Read Full Briefing →</button>
    <div id="ops-briefing-full" class="hidden" style="margin-top:12px;">
      <div class="report-narrative report" style="max-height:420px; overflow-y:auto;">${esc(data.markdown)}</div>
    </div>
  `;
  $("ops-briefing-toggle").addEventListener("click", () => {
    const full = $("ops-briefing-full");
    const nowHidden = full.classList.toggle("hidden");
    $("ops-briefing-toggle").textContent = nowHidden ? "Read Full Briefing →" : "Hide Full Briefing";
  });
}

async function renderOperations(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Operations — Month ${snap.month} ${helpIcon("operations")}</h2>
    <p style="font-size:12px; color:var(--text-low); margin-bottom:16px;">How your company actually runs day to day — the operating model, capital equipment, R&amp;D pipeline, market reach, and cost base. Individual products (and their factories) live under the Products tab.</p>
    <div id="operations-briefing" style="margin-bottom:26px;"></div>
    <div class="commands">
      <button id="btn-configure-ops">Configure Operations</button>
      <button id="btn-buy-equipment">Buy Equipment</button>
      <button id="btn-marketing-campaign">Marketing Campaign</button>
      <button id="btn-invest-rnd">Invest in R&amp;D</button>
      <button id="btn-expand-market">Expand Market</button>
      <button id="btn-cut-costs">Cut Costs</button>
    </div>
    <div id="ops-config-summary" style="font-size:12px; color:var(--text-low); margin-bottom:20px;"></div>
  `;
  wireCommandButton($("btn-buy-equipment"), "capex");
  wireCommandButton($("btn-marketing-campaign"), "marketing");
  wireCommandButton($("btn-invest-rnd"), "rnd");
  $("btn-expand-market").addEventListener("click", openExpandMarketModal);
  $("btn-cut-costs").addEventListener("click", () => openAmountModal("Cut Costs", "Cut R&D / S&M / G&A by what % (0-50)?", async (pctVal) => {
    const pct = Math.max(0, Math.min(50, pctVal)) / 100;
    const res = await postJSON("/commands/cut-costs", { pct });
    await window.refreshAll();
    toast(res.message);
  }, true, 20, `Current opex: R&D ${fmtMoney(window.__snapshot.income_statement.rnd)} · S&M ${fmtMoney(window.__snapshot.income_statement.snm)} · G&A ${fmtMoney(window.__snapshot.income_statement.gna)}`));
  renderOperationsBriefing();
  const opsConfig = await (await fetch(`${API}/operations/config`)).json();
  $("btn-configure-ops").addEventListener("click", async () => {
    const opsSchema = await (await fetch(`${API}/operations/schema`)).json();
    openOperationsConfigModal(opsSchema, opsConfig);
  });
  const opsEntries = Object.entries(opsConfig.config || {});
  $("ops-config-summary").innerHTML = opsEntries.length
    ? `Current operating model: ${opsEntries.map(([k, v]) => `<strong>${esc(v)}</strong> (${esc(k)})`).join(" · ")}`
    : "No operating model configured yet — click Configure Operations to set how your company actually runs.";
  const dynamics = await (await fetch(`${API}/industry-dynamics`)).json();
  if (dynamics.mechanic) {
    const metricLine = Object.entries(dynamics).filter(([k]) => !["mechanic", "note"].includes(k))
      .map(([k, v]) => `${k.replace(/_/g, " ")}: <strong>${typeof v === "number" ? v : esc(String(v))}</strong>`).join(" · ");
    $("ops-config-summary").innerHTML += `<div style="margin-top:10px; padding:10px 12px; border:1px solid var(--line); border-radius:8px;">
      <div style="font-size:10px; color:var(--brass); font-family:var(--font-display); font-weight:700; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:4px;">⚡ ${esc(dynamics.mechanic)}</div>
      <div style="font-family:var(--font-mono); font-size:11.5px; margin-bottom:4px;">${metricLine}</div>
      <div style="color:var(--text-low);">${esc(dynamics.note)}</div>
    </div>`;
  }

  // Billable Delivery Capacity — real capacity/utilization state, distinct
  // from the flagship-mechanic flavor panel above: this is a decision-
  // relevant readout (bench cost vs. overtime pressure) for the two
  // people-delivered-service industries, not narrative color.
  const deliveryCap = await (await fetch(`${API}/delivery-capacity`)).json();
  if (deliveryCap) {
    renderDeliveryCapacityPanel(deliveryCap, window.__company);
  }

  // Room Capacity — the same idea as Delivery Capacity above, but for
  // Hospitality & Travel: a real, distinct capacity model (physical
  // rooms, not billable hours), derived from the same Operations
  // headcount pipeline.
  const roomCap = await (await fetch(`${API}/room-capacity`)).json();
  if (roomCap) {
    renderRoomCapacityPanel(roomCap);
  }

  // Fab Capacity — Semiconductor & Hardware's own capacity model: wafer
  // starts/month at a sticky physical fab (not headcount-derived like the
  // two panels above), and unlike them it carries a real player decision —
  // committing capex to expand — so the alert is never a dead end.
  // Guarded on a real numeric field rather than bare truthiness: the
  // backend contract is "null = not applicable", but a stray empty object
  // must never render a half-built panel.
  const fabCap = await (await fetch(`${API}/fab-capacity`)).json();
  if (fabCap && typeof fabCap.utilization_pct === "number") {
    renderFabCapacityPanel(fabCap);
  }
}

function renderRoomCapacityPanel(cap) {
  const bandCopy = {
    critical: { color: "var(--oxblood)", text: "Overbooked — overtime housekeeping and rush F&B staffing are raising your cost of goods, and morale is taking a real hit." },
    pressure: { color: "var(--neon-gold)", text: "Approaching full occupancy — a bit more demand and this tips into overtime territory." },
    healthy: { color: "var(--sage)", text: "Right in the healthy occupancy band." },
    underutilized: { color: "var(--text-low)", text: "Well under target occupancy — idle housekeeping/front-desk payroll is dragging on margin." },
    no_capacity: { color: "var(--text-low)", text: "No rooms staffed yet — hire Operations headcount to open rooms for booking." },
  }[cap.band] || { color: "var(--text-low)", text: "" };

  const pct = Math.min(150, cap.occupancy_pct);
  const targetPos = Math.min(100, cap.target_occupancy_pct / 1.5);

  const panel = document.createElement("div");
  panel.style.cssText = "margin-top:16px; padding:16px; border:1px solid var(--line); border-radius:var(--radius); background:var(--bg-1);";
  panel.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px;">
      <div style="font-family:var(--font-display); font-size:15px; font-weight:700;">Room Capacity &amp; Occupancy</div>
      <div style="font-family:var(--font-mono); font-size:20px; color:${bandCopy.color};">${cap.occupancy_pct.toFixed(1)}%</div>
    </div>
    <div style="position:relative; height:10px; border-radius:6px; background:var(--line); margin-bottom:6px; overflow:visible;">
      <div style="position:absolute; left:0; top:0; height:100%; width:${Math.min(100, pct / 1.5)}%; border-radius:6px; background:${bandCopy.color};"></div>
      <div title="Target: ${cap.target_occupancy_pct.toFixed(0)}%" style="position:absolute; left:${targetPos}%; top:-3px; width:2px; height:16px; background:var(--text-hi);"></div>
    </div>
    <div style="font-size:11px; color:var(--text-low); margin-bottom:12px;">${esc(bandCopy.text)}</div>
    <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(110px, 1fr)); gap:10px 16px; font-size:11.5px;">
      <div><div style="color:var(--text-low); font-size:10.5px;">Rooms</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.rooms)}${cap.headcount_in_pipeline ? ` <span style="color:var(--text-low); font-size:11px;">(+${cap.headcount_in_pipeline} staff hiring)</span>` : ""}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">ADR</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtMoney(cap.adr)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">RevPAR</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtMoney(cap.revpar)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">${cap.overbooked_room_nights > 0 ? "Overbooked (room-nights)" : "Vacant (room-nights)"}</div><div style="font-family:var(--font-mono); font-size:14px;" class="${cap.overbooked_room_nights > 0 ? "neg" : ""}">${fmtNum(cap.overbooked_room_nights > 0 ? cap.overbooked_room_nights : cap.vacant_room_nights)}</div></div>
    </div>
    <div style="font-size:10.5px; color:var(--text-low); margin-top:10px; padding-top:10px; border-top:1px solid var(--line-soft);">
      Room count is driven automatically by your Operations headcount (housekeeping/front-desk/F&B) — the same hiring pipeline as every department, sized off a realistic rooms-per-staff ratio. To change room count, adjust overall cost posture via Cut Costs or grow revenue faster than the hiring pipeline can track.
    </div>
  `;
  $("ops-config-summary").after(panel);
}

function _fabBandCopy(cap) {
  const util = cap.utilization_pct.toFixed(0);
  const pending = cap.pending_expansion_wspm > 0;
  const arriving = pending
    ? ` ${fmtNum(cap.pending_expansion_wspm)} WSPM is already under construction and arrives in ${cap.months_to_next_commission} month${cap.months_to_next_commission === 1 ? "" : "s"}.`
    : "";
  return {
    critical: {
      color: "var(--oxblood)",
      headline: `Critical capacity pressure — the fab is at ${util}% of what it can sustain.`,
      text: `Demand is running ${fmtNum(cap.unserved_wspm)} wafer starts/month above effective capacity. Expedite and overtime premiums are raising your cost of goods and fab staff are strained. A new fab takes months to commission, so relief is slow.${arriving}`,
    },
    pressure: {
      color: "var(--neon-gold)",
      headline: `Capacity watch — the fab is at ${util}%.`,
      text: `Approaching the ceiling: a little more demand tips this into overtime and expedite territory. Expansion takes months to arrive, so the time to decide is before it turns critical.${arriving}`,
    },
    healthy: {
      color: "var(--sage)",
      headline: "Utilization is in the healthy band.",
      text: `The fab has room to absorb demand without premium costs.${arriving}`,
    },
    underutilized: {
      color: "var(--text-low)",
      headline: `Idle capacity — the fab is only at ${util}%.`,
      text: `${fmtNum(cap.idle_wspm)} wafer starts/month of installed capacity are idle, but cleanroom, tool depreciation and utilities are fixed costs, so unabsorbed overhead is dragging on margin. The fab does not shrink in a downturn.${arriving}`,
    },
    no_capacity: {
      color: "var(--text-low)",
      headline: "No fab capacity installed.",
      text: "Commit capex to build wafer-start capacity.",
    },
  }[cap.band] || { color: "var(--text-low)", headline: "", text: "" };
}

function renderFabCapacityPanel(cap) {
  const copy = _fabBandCopy(cap);
  const pct = Math.min(150, cap.utilization_pct);
  const targetPos = Math.min(100, cap.target_utilization_pct / 1.5);

  const panel = document.createElement("div");
  panel.id = "fab-capacity-panel";
  panel.style.cssText = "margin-top:16px; padding:16px; border:1px solid var(--line); border-radius:var(--radius); background:var(--bg-1);";
  panel.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px;">
      <div style="font-family:var(--font-display); font-size:15px; font-weight:700;">Fab Capacity &amp; Wafer Utilization</div>
      <div style="font-family:var(--font-mono); font-size:20px; color:${copy.color};">${cap.utilization_pct.toFixed(1)}%</div>
    </div>
    <div style="position:relative; height:10px; border-radius:6px; background:var(--line); margin-bottom:6px; overflow:visible;">
      <div style="position:absolute; left:0; top:0; height:100%; width:${Math.min(100, pct / 1.5)}%; border-radius:6px; background:${copy.color};"></div>
      <div title="Sizing target: ${cap.target_utilization_pct.toFixed(0)}% of nameplate" style="position:absolute; left:${targetPos}%; top:-3px; width:2px; height:16px; background:var(--text-hi);"></div>
    </div>
    <div style="font-size:12px; font-weight:600; color:${copy.color}; margin-bottom:2px;">${esc(copy.headline)}</div>
    <div style="font-size:11px; color:var(--text-low); margin-bottom:12px;">${esc(copy.text)}</div>
    <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(120px, 1fr)); gap:10px 16px; font-size:11.5px;">
      <div><div style="color:var(--text-low); font-size:10.5px;">Nameplate (WSPM)</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.nameplate_wspm)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">Effective incl. ${cap.flex_headroom_pct.toFixed(0)}% flex</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.effective_wspm)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">Demand (WSPM)</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.demand_wspm)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">${cap.unserved_wspm > 0 ? "Unserved (WSPM)" : "Idle (WSPM)"}</div><div style="font-family:var(--font-mono); font-size:14px;" class="${cap.unserved_wspm > 0 ? "neg" : ""}">${fmtNum(cap.unserved_wspm > 0 ? cap.unserved_wspm : cap.idle_wspm)}</div></div>
    </div>
    <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; margin-top:12px; padding-top:12px; border-top:1px solid var(--line-soft);">
      <div style="font-size:10.5px; color:var(--text-low); flex:1;">
        A fab is a physical asset: capacity does not shrink when the chip cycle turns down, and it only grows through real capex that takes months to commission.
      </div>
      <button class="primary" id="btn-expand-fab" style="white-space:nowrap;">Expand Fab</button>
    </div>
  `;
  $("ops-config-summary").after(panel);
  panel.querySelector("#btn-expand-fab").addEventListener("click", () => openExpandFabModal(cap));
}

// Opens the expansion decision with a LIVE consequence preview: what it
// costs, when the capacity actually arrives, and what it does to utilization
// — shown before the player commits, and recomputed as they type.
function _fabExpansionPreview(cap, addedWspm) {
  const added = Math.max(0, Number(addedWspm) || 0);
  const cost = added * cap.expansion_cost_per_wspm;
  const newEffective = (cap.nameplate_wspm + cap.pending_expansion_wspm + added) * (1 + cap.flex_headroom_pct / 100);
  const projectedUtil = newEffective > 0 ? (cap.demand_wspm / newEffective) * 100 : 0;
  return { added, cost, projectedUtil };
}

function openExpandFabModal(cap) {
  const suggested = Math.max(10, Math.round(cap.nameplate_wspm * 0.15));
  const months = cap.construction_months;
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.id = "expand-fab-modal";
  backdrop.innerHTML = `
    <div class="modal">
      <h3>Expand Fab Capacity</h3>
      <div style="font-size:11.5px; color:var(--text-mid); background:var(--bg-1); border:1px solid var(--line); border-radius:var(--radius); padding:10px 12px; margin-bottom:16px; line-height:1.5;">
        Cash leaves your balance <strong>now</strong>. The capacity itself only arrives after <strong>${months} months</strong> of construction and tool qualification — you stay exposed to today's pressure until then.
      </div>
      ${sliderField("fab-added-wspm", "Wafer starts per month to add", { min: 10, max: Math.max(500, Math.round(cap.nameplate_wspm * 1.5)), step: 1, value: suggested, unit: "wafers/mo" })}
      <div id="fab-preview" style="font-size:12px; line-height:1.7; margin-bottom:8px;"></div>
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">Commit the equipment spend</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  const input = backdrop.querySelector("#fab-added-wspm");
  const preview = backdrop.querySelector("#fab-preview");
  const refresh = () => {
    const p = _fabExpansionPreview(cap, input.value);
    preview.innerHTML = `
      <div><span style="color:var(--text-low);">Cost today:</span> <strong style="font-family:var(--font-mono);">${fmtMoney(p.cost)}</strong></div>
      <div><span style="color:var(--text-low);">Capacity arrives in:</span> <strong>${months} months</strong></div>
      <div><span style="color:var(--text-low);">Utilization at today's demand once commissioned:</span> <strong style="font-family:var(--font-mono);">${p.projectedUtil.toFixed(0)}%</strong></div>
      <div style="color:var(--text-low); font-size:11px;">Bigger is not automatically better: an oversized fab sits idle through the next downturn and its fixed costs keep running.</div>
    `;
  };
  input.addEventListener("input", refresh);
  refresh();
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const added = parseFloat(input.value) || 0;
    backdrop.remove();
    const res = await postJSON("/commands/expand-fab", { added_wspm: added });
    await window.refreshAll();
    // The endpoint reports ok=false for a refusal (not enough cash, below the
    // minimum). toast() only styles a message as a warning if it matches a
    // wording regex, and these refusal messages don't ("Not enough cash..."),
    // so a refused expansion would otherwise look like a success and the
    // player would believe they had spent money they had not.
    toast(res.message, !res.ok);
  });
}

function renderDeliveryCapacityPanel(cap, company) {
  const isLegal = company && company.industry === "Legal Services";
  const roleLabel = isLegal ? "Attorneys" : "Consultants";
  const bandCopy = {
    critical: { color: "var(--oxblood)", text: "Over capacity — backlog is building and overtime/contractor costs are raising your cost of goods." },
    pressure: { color: "var(--neon-gold)", text: "Approaching the ceiling — a bit more demand and this tips into overtime territory." },
    healthy: { color: "var(--sage)", text: "Right in the healthy operating band." },
    underutilized: { color: "var(--text-low)", text: "Well under target — idle payroll (bench cost) is dragging on margin." },
    no_capacity: { color: "var(--text-low)", text: "No delivery headcount yet — hire to start converting demand into revenue." },
  }[cap.band] || { color: "var(--text-low)", text: "" };

  const pct = Math.min(150, cap.utilization_pct); // visual cap so the bar doesn't run off the container past 150%
  const targetPos = Math.min(100, cap.target_utilization_pct / 1.5);

  const panel = document.createElement("div");
  panel.style.cssText = "margin-top:16px; padding:16px; border:1px solid var(--line); border-radius:var(--radius); background:var(--bg-1);";
  panel.innerHTML = `
    <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px;">
      <div style="font-family:var(--font-display); font-size:15px; font-weight:700;">Billable Delivery Capacity</div>
      <div style="font-family:var(--font-mono); font-size:20px; color:${bandCopy.color};">${cap.utilization_pct.toFixed(1)}%</div>
    </div>
    <div style="position:relative; height:10px; border-radius:6px; background:var(--line); margin-bottom:6px; overflow:visible;">
      <div style="position:absolute; left:0; top:0; height:100%; width:${Math.min(100, pct / 1.5)}%; border-radius:6px; background:${bandCopy.color};"></div>
      <div title="Target: ${cap.target_utilization_pct.toFixed(0)}%" style="position:absolute; left:${targetPos}%; top:-3px; width:2px; height:16px; background:var(--text-hi);"></div>
    </div>
    <div style="font-size:11px; color:var(--text-low); margin-bottom:12px;">${esc(bandCopy.text)}</div>
    <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(110px, 1fr)); gap:10px 16px; font-size:11.5px;">
      <div><div style="color:var(--text-low); font-size:10.5px;">${esc(roleLabel)}</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.headcount)}${cap.headcount_in_pipeline ? ` <span style="color:var(--text-low); font-size:11px;">(+${cap.headcount_in_pipeline} hiring)</span>` : ""}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">Available Hours/mo</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.available_hours)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">Demand Hours/mo</div><div style="font-family:var(--font-mono); font-size:14px;">${fmtNum(cap.demand_hours)}</div></div>
      <div><div style="color:var(--text-low); font-size:10.5px;">${cap.backlog_hours > 0 ? "Backlog Hours" : "Bench Hours"}</div><div style="font-family:var(--font-mono); font-size:14px;" class="${cap.backlog_hours > 0 ? "neg" : ""}">${fmtNum(cap.backlog_hours > 0 ? cap.backlog_hours : cap.bench_hours)}</div></div>
    </div>
    <div style="font-size:10.5px; color:var(--text-low); margin-top:10px; padding-top:10px; border-top:1px solid var(--line-soft);">
      Headcount is driven automatically by your operations spend (cost of goods) — the same hiring pipeline as every department, just sized off billable-hour economics for ${esc(roleLabel.toLowerCase())} instead of a generic Ops budget. To change headcount, adjust overall cost posture via Cut Costs or grow revenue faster than the hiring pipeline can track.
    </div>
  `;
  $("ops-config-summary").after(panel);
}

// Shared body for Build Factory / Expand Capacity: two linked sliders
// (capacity requested, investment $) plus a live cost-per-unit and
// utilization-impact readout. Capacity and investment are both
// player-chosen (the backend doesn't derive one from the other), so the
// "effect" here is a sanity-check preview: what does this cost per unit
// look like, and how does it move utilization for THIS product right now.
function openFactoryCapacityModal(opts) {
  const {
    headerTitle, description, confirmLabel, capacityDefault, capacityMax,
    investmentDefault, currentCapacity, currentUtilizationPct, onConfirm,
  } = opts;
  const s = window.__snapshot;
  const cash = (s && s.balance_sheet && s.balance_sheet.CASH) || 0;
  // Strictly cash-capped: cmd_build_factory/cmd_upgrade_factory silently
  // clamp investment to available cash server-side, so letting the slider
  // go higher would preview a cost-per-unit and utilization outcome the
  // server would never actually deliver.
  const invMax = Math.max(1000, cash);

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal" style="width:min(560px, 92vw);">
      <h3>${esc(headerTitle)}</h3>
      ${description ? `<div style="font-size:11.5px; color:var(--text-mid); background:var(--bg-1); border:1px solid var(--line); border-radius:var(--radius); padding:10px 12px; margin-bottom:16px; line-height:1.5;">${esc(description)}</div>` : ""}
      <div class="field">
        <label>Capacity to add (units/month)</label>
        <input type="range" class="invest-slider" id="fc-capacity-range" min="1" max="${capacityMax}" step="${Math.max(1, Math.round(capacityMax / 200))}" value="${capacityDefault}" />
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <span class="invest-slider-amount" id="fc-capacity-amount" style="font-size:16px;"></span>
          <input type="hidden" id="fc-capacity-exact" value="${capacityDefault}" />
        </div>
      </div>
      <div class="invest-slider-wrap">
        <div class="invest-slider-amount-row">
          <div class="invest-slider-amount" id="fc-invest-amount"></div>
          <div class="invest-slider-cap">Cash available: ${fmtMoney(cash)}</div>
        </div>
        <input type="range" class="invest-slider" id="fc-invest-range" min="0" max="${invMax}" step="${Math.max(1, Math.round(invMax / 200))}" value="${Math.min(investmentDefault, invMax)}" />
        <input type="hidden" id="fc-invest-exact" value="${Math.min(investmentDefault, invMax)}" />
      </div>
      <div class="invest-effect-panel" id="fc-effect"></div>
      ${opts.ghostAction ? `<div class="sx-forecast" id="fc-ghost"></div>` : ""}
      <div class="modal-actions">
        <button id="modal-cancel">Cancel</button>
        <button class="primary" id="modal-ok">${esc(confirmLabel)}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  const capRange = backdrop.querySelector("#fc-capacity-range");
  const capExact = backdrop.querySelector("#fc-capacity-exact");
  const capAmount = backdrop.querySelector("#fc-capacity-amount");
  const invRange = backdrop.querySelector("#fc-invest-range");
  const invExact = backdrop.querySelector("#fc-invest-exact");
  const invAmount = backdrop.querySelector("#fc-invest-amount");
  const effectEl = backdrop.querySelector("#fc-effect");
  const ghost = opts.ghostAction ? window.uiGhostPanel(backdrop.querySelector("#fc-ghost")) : null;

  function render() {
    const capacity = parseInt(capExact.value) || 0;
    const investment = parseFloat(invExact.value) || 0;
    if (ghost) ghost.run(capacity > 0 && investment > 0 ? opts.ghostAction(capacity, investment) : null);
    capAmount.textContent = `${fmtNum(capacity)} units/mo`;
    invAmount.textContent = fmtMoney(investment);
    const costPerUnit = capacity > 0 ? investment / capacity : 0;
    const newCapacity = currentCapacity + capacity;
    // demandRevenueProxy is back-derived from today's utilization so the
    // preview uses the exact same demand_revenue/factory_max_revenue
    // relationship the backend uses, just solved for the new capacity.
    const demandProxy = currentCapacity > 0 ? (currentUtilizationPct / 100) * currentCapacity : capacity * 0.5;
    const newUtilPct = newCapacity > 0 ? Math.min(999, (demandProxy / newCapacity) * 100) : 0;
    effectEl.innerHTML = `
      <div class="invest-effect-title">Projected Effect</div>
      <div class="invest-effect-grid">
        <div><div class="invest-effect-stat-label">Cost per Unit</div><div class="invest-effect-stat-value">${fmtMoney(costPerUnit)}</div></div>
        <div><div class="invest-effect-stat-label">New Total Capacity</div><div class="invest-effect-stat-value">${fmtNum(newCapacity)}/mo</div></div>
        <div><div class="invest-effect-stat-label">Est. Utilization After</div><div class="invest-effect-stat-value ${newUtilPct > 90 ? "neg" : "pos"}">${newUtilPct.toFixed(0)}%</div></div>
      </div>
      <div class="invest-effect-note ${investment >= cash && cash > 0 ? "warn" : ""}">${
        investment >= cash && cash > 0
          ? "This spends all available cash — nothing left over this month."
          : "Utilization estimate uses this month's demand held constant — actual demand will keep moving."
      }</div>
    `;
  }

  function syncCap(v) { v = Math.min(Math.max(parseInt(v) || 0, 1), capacityMax); capRange.value = v; capExact.value = v; render(); }
  function syncInv(v) { v = Math.min(Math.max(parseFloat(v) || 0, 0), invMax); invRange.value = v; invExact.value = v; render(); }

  capRange.addEventListener("input", () => syncCap(capRange.value));
  capExact.addEventListener("input", () => syncCap(capExact.value));
  invRange.addEventListener("input", () => syncInv(invRange.value));
  invExact.addEventListener("input", () => syncInv(invExact.value));
  render();

  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const capacity_units = parseInt(capExact.value) || 0;
    const investment = parseFloat(invExact.value) || 0;
    backdrop.remove();
    if (capacity_units <= 0 || investment <= 0) return;
    await onConfirm(capacity_units, investment);
  });
}

function openBuildFactoryModal(productId, productName, label, description) {
  // No factory exists yet for this product, so there's no utilization
  // history to project from — the effect panel shows cost-per-unit only
  // until the factory exists.
  openFactoryCapacityModal({
    headerTitle: `${label || "Build Factory"} — ${productName}`,
    description: `${description ? description + " " : ""}Capacity becomes a hard ceiling on this product's revenue. A Standard-tier supplier is assigned by default — switch it anytime. Once built, this replaces the quality-investment lever with Automation.`,
    confirmLabel: label || "Build",
    capacityDefault: 5000,
    capacityMax: 50000,
    investmentDefault: 300000,
    currentCapacity: 0,
    currentUtilizationPct: 0,
    ghostAction: (capacity_units, investment) => ({ type: "build_factory", params: { product_name: productName, capacity_units, investment } }),
    onConfirm: async (capacity_units, investment) => {
      const res = await postJSON("/factories/build", { product_id: productId, capacity_units, investment });
      await window.refreshAll();
      toast(res.message);
    },
  });
}

function openUpgradeFactoryModal(factoryId, productName, factory) {
  const currentCapacity = factory ? (factory.capacity_units || 0) : 0;
  const currentUtilizationPct = factory ? (factory.utilization_pct || 0) : 0;
  openFactoryCapacityModal({
    headerTitle: `Expand Capacity — ${productName}`,
    description: null,
    confirmLabel: "Expand",
    capacityDefault: 2000,
    capacityMax: Math.max(20000, currentCapacity),
    investmentDefault: 150000,
    currentCapacity,
    currentUtilizationPct,
    onConfirm: async (additional_capacity, investment) => {
      const res = await postJSON("/factories/upgrade", { factory_id: factoryId, additional_capacity, investment });
      await window.refreshAll();
      toast(res.message);
    },
  });
}

// --- Shared config-page helpers (Launch Product / Configure Operations) ----

// Mirrors industries.score_selections exactly: the option's "key" IS its
// tier_index (0-4) as a string, and only the FIRST category in a schema is
// the 1.5x-weighted "defining" dimension — see industries.py for why.
const TIER_TRADEOFF_DESC = [
  "Cheapest to build. Ramps fastest on a small budget, but caps out lowest.",
  "Balanced cost and ceiling — the default most companies start with.",
  "Costs more upfront. Meaningfully raises the ceiling and growth rate.",
  "Expensive. Strong ceiling, aimed at higher-value segments.",
  "Highest cost. Maximum ceiling and growth rate, but eats hardest into your budget.",
];

function _computeAvgTier(backdrop, inputPrefix) {
  let totalWeighted = 0, totalWeight = 0;
  backdrop.querySelectorAll(`.config-dimension`).forEach((dim, i) => {
    const checked = dim.querySelector(`input[name="${inputPrefix}-${i}"]:checked`);
    const key = checked ? parseInt(checked.value) : 1;
    const weight = i === 0 ? 1.5 : 1.0;
    totalWeighted += (key / 4) * weight;
    totalWeight += weight;
  });
  return totalWeight > 0 ? totalWeighted / totalWeight : 0.4;
}

function renderConfigDimension(cat, idx, inputPrefix, selectedKey, roleNoun) {
  const cards = cat.options.map((o) => {
    const checked = String(o.key) === String(selectedKey);
    return `
      <label class="option-card${checked ? " selected" : ""}">
        <input type="radio" name="${inputPrefix}-${idx}" value="${o.key}" ${checked ? "checked" : ""} />
        <span class="option-card-tier">${esc(o.tier_label)}</span>
        <span class="option-card-label">${esc(o.label)}</span>
        <span class="option-card-desc">${esc(TIER_TRADEOFF_DESC[o.tier_index] ?? "")}</span>
      </label>`;
  }).join("");
  return `
    <div class="config-dimension" data-category="${esc(cat.category)}">
      <div class="config-dimension-header">
        <span class="config-dimension-title">${esc(cat.category)}</span>
        ${idx === 0
          ? `<span class="config-dimension-defining">★ Defines this ${roleNoun} — weighted 1.5x in scoring</span>`
          : `<span class="config-dimension-role">Supporting choice — fine-tunes the trade-off</span>`}
      </div>
      <div class="option-card-row">${cards}</div>
    </div>`;
}

function _wireOptionCardClicks(backdrop, onChange) {
  backdrop.querySelectorAll(".option-card input").forEach((input) => {
    input.addEventListener("change", () => {
      backdrop.querySelectorAll(`input[name="${input.name}"]`).forEach((sib) => {
        sib.closest(".option-card").classList.toggle("selected", sib.checked);
      });
      onChange();
    });
  });
}

// openLaunchProductModal lives in sections/launch.js (the Product Studio flow).

function openOperationsConfigModal(schema, current) {
  const currentConfig = (current && current.config) || {};
  const priorTier = (current && current.avg_tier) || 0.4;
  const currentRevenue = (window.__snapshot && window.__snapshot.income_statement && window.__snapshot.income_statement.revenue) || 0;
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal modal-page">
      <div class="modal-page-header">
        <h3>Configure Operations</h3>
        <p>How your company actually runs, in real terms for your industry — manufacturing/infrastructure model, workforce, supply chain, quality &amp; compliance, and distribution. A leaner setup runs at a lower cost base but caps how far growth can go; a heavily built-out setup costs more of revenue but raises the ceiling. Re-configuring has a one-time re-tooling cost proportional to how big the change is and your current revenue run-rate.</p>
      </div>
      <div class="modal-page-body">
        <div class="projected-profile" id="ops-preview"></div>
        ${schema.categories.map((cat, idx) => {
          const currentLabel = currentConfig[cat.category];
          const currentOpt = cat.options.find((o) => o.label === currentLabel);
          const defaultKey = currentOpt ? currentOpt.key : "1";
          return renderConfigDimension(cat, idx, "ops", defaultKey, "operating model");
        }).join("")}
      </div>
      <div class="modal-page-footer">
        <div style="font-size:11px; color:var(--text-low);">Applies immediately on confirm.</div>
        <div class="modal-actions">
          <button id="modal-cancel">Cancel</button>
          <button class="primary" id="modal-ok">Apply Configuration</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  const updatePreview = () => {
    const avgTier = _computeAvgTier(backdrop, "ops");
    const cogsAdjust = (-0.05 + avgTier * 0.10) * 100;
    const growthAdjust = (-0.004 + avgTier * 0.014) * 100;
    const tierDelta = Math.abs(avgTier - priorTier);
    const retoolCost = tierDelta * 150000 * (1 + currentRevenue / 500000);
    const tierWord = avgTier > 0.7 ? "Flagship" : avgTier > 0.5 ? "Premium" : avgTier > 0.25 ? "Standard" : "Lean";
    backdrop.querySelector("#ops-preview").innerHTML = `
      <div class="projected-profile-title">Projected Impact — ${tierWord} Operating Model</div>
      <div><div class="projected-stat-label">Cost of goods</div><div class="projected-stat-value">${cogsAdjust >= 0 ? "+" : ""}${cogsAdjust.toFixed(1)}pt</div></div>
      <div><div class="projected-stat-label">Growth Impact</div><div class="projected-stat-value">${growthAdjust >= 0 ? "+" : ""}${growthAdjust.toFixed(2)}pt/mo</div></div>
      <div><div class="projected-stat-label">Re-Tooling Cost (if applied now)</div><div class="projected-stat-value">${fmtMoney(retoolCost)}</div></div>
    `;
  };
  _wireOptionCardClicks(backdrop, updatePreview);
  updatePreview();

  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#modal-ok").addEventListener("click", async () => {
    const selections = {};
    schema.categories.forEach((cat, idx) => {
      const checked = backdrop.querySelector(`input[name="ops-${idx}"]:checked`);
      selections[cat.category] = checked ? checked.value : "1";
    });
    backdrop.remove();
    const res = await postJSON("/operations/configure", { selections });
    await window.refreshAll();
    toast(res.message);
  });
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderOperations = renderOperations;

export { HEALTH_COLOR, _wireOptionCardClicks, openBuildFactoryModal, openUpgradeFactoryModal, renderConfigDimension, renderDeliveryCapacityPanel, renderFabCapacityPanel, renderRoomCapacityPanel };
